<template>
  <div
    ref="editorRoot"
    class="relative editor-root"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <tag-agents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      :fixed-position="true"
      @click="insertMentionNode"
    />
    <canned-response
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="insertCannedResponse"
    />
    <variable-list
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      @click="insertVariable"
    />
    <input
      ref="imageUpload"
      type="file"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
      hidden
      @change="onFileChange"
    />
    <div
      ref="editor"
      class="relative editor-container"
      :class="{
        'hide-toolbar': !showToolbarInternal,
        'typing-direction-rtl': typingDirection === 'rtl',
        'typing-direction-ltr': typingDirection === 'ltr',
      }"
    />
    <slot name="warning-message" />
    <div
      v-show="isImageNodeSelected && showImageResizeToolbar"
      class="absolute shadow-md rounded-[4px] flex gap-1 py-1 px-1 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-50"
      :style="{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
      }"
    >
      <button
        v-for="size in sizes"
        :key="size.name"
        class="text-xs font-medium rounded-[4px] border border-solid border-slate-200 dark:border-slate-600 px-1.5 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
        @click="setURLWithQueryAndImageSize(size)"
      >
        {{ size.name }}
      </button>
    </div>
    <div class="message-toolbar">
      <div
        v-if="isEmailOrWebWidgetInbox && hasAttachments"
        class="flex flex-col px-4 py-2 overflow-y-auto custom-scroll max-h-[148px] border-t border-gray-200"
      >
        <editor-attachments-tray
          class="[&>.preview-item]:dark:bg-slate-700 flex-row flex-wrap gap-x-3 gap-y-1"
          :attachments="attachedFiles"
          :remove-attachment="removeAttachment"
        />
      </div>
      <div
        class="flex flex-row items-center gap-[10px] px-4 py-2 border-t border-gray-200"
      >
        <div class="relative flex">
          <span
            ref="emojiTrigger"
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            class="material-symbols-outlined message-toolbar-item"
            @click.stop="toggleEmojiPicker"
          >
            sentiment_satisfied
          </span>
          <emoji-input
            v-show="showEmojiPicker"
            v-on-clickaway="onClickAwayEmojiPicker"
            :class="emojiDialogClassOnRTLView"
            :on-click="emojiOnClick"
            :trigger-el="$refs.emojiTrigger"
            :visible="showEmojiPicker"
          />
        </div>
        <span
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_FORMATTING_TOOLBAR')"
          class="material-symbols-outlined message-toolbar-item"
          :class="{ active: showToolbarInternal }"
          @click="toggleEditorToolbar"
        >
          custom_typography
        </span>
        <span
          v-if="typingDirection === 'rtl'"
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_TEXT_DIRECTION')"
          class="material-symbols-outlined message-toolbar-item"
          @click="switchTypingDirection('ltr')"
        >
          format_textdirection_l_to_r
        </span>
        <span
          v-else
          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_TEXT_DIRECTION')"
          class="material-symbols-outlined message-toolbar-item"
          @click="switchTypingDirection('rtl')"
        >
          format_textdirection_r_to_l
        </span>
        <slot name="toolbar-left" />
        <div class="flex-1" />
        <slot name="toolbar-right" />
      </div>
    </div>
  </div>
</template>

<script>
import { setBlockType } from 'prosemirror-commands';
import { DOMParser as PmDOMParser, DOMSerializer } from 'prosemirror-model';
import { mixin as clickaway } from 'vue-clickaway';
import Index from './Index.vue';
import { messageSchema, EditorState, buildEditor, EditorView } from './config';
import HtmlBlockView, { resolveExecCommand } from './HtmlBlockView';

import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import EditorAttachmentTray from 'dashboard/components/widgets/WootWriter/EditorAttachmentTray.vue';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

export default {
  name: 'WootMessageEditorV2',
  components: {
    EmojiInput,
    EditorAttachmentsTray: EditorAttachmentTray,
  },
  extends: Index,
  mixins: [clickaway, inboxMixin, rtlMixin],
  props: {
    showToolbar: {
      type: Boolean,
      default: false,
    },
    // Inbox — provides channel type context for inboxMixin
    inbox: {
      type: Object,
      default: () => ({}),
    },
    // Emoji — received from parent to share a single picker state
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    hideEmojiPicker: {
      type: Function,
      default: () => {},
    },
    // Attachments — received from parent so the tray can display them
    attachedFiles: {
      type: Array,
      default: () => [],
    },
    removeAttachment: {
      type: Function,
      default: () => {},
    },
    autoDetectLanguage: {
      type: Boolean,
      default: false,
    },
    useHtmlMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showToolbarInternal: this.showToolbar,
      typingDirection: 'ltr',
      htmlBlockView: null,
      htmlBlockViews: [],
      toolbarHandler: null,
    };
  },
  computed: {
    defaultDirection() {
      return this.isRTLView ? 'rtl' : 'ltr';
    },
    contentFromEditor() {
      if (this.useHtmlMode) {
        return this.serializeToHtml();
      }
      return this.$options.extends.computed.contentFromEditor.call(this);
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailChannel || this.channelType === INBOX_TYPES.WEB;
    },
    hasAttachments() {
      return this.attachedFiles.length > 0;
    },
    emojiDialogClassOnRTLView() {
      return this.isRTLView ? 'emoji-dialog--rtl' : '';
    },
  },
  created() {
    if (this.useHtmlMode) {
      this.state = this.createHtmlState(this.value);
    }
  },
  mounted() {
    this.typingDirection = this.isRTLView ? 'rtl' : 'ltr';
    if (this.isRTLView) {
      this.setDocumentDirection('rtl');
    }
  },
  beforeDestroy() {
    this.teardownToolbarInterception();
  },
  methods: {
    createHtmlState(html) {
      const container = document.createElement('div');
      container.innerHTML = html || '';
      return EditorState.create({
        doc: PmDOMParser.fromSchema(messageSchema).parse(container),
        plugins: buildEditor({
          schema: messageSchema,
          placeholder: this.placeholder,
          methods: { onImageUpload: this.openFileBrowser },
          plugins: this.plugins,
          enabledMenuOptions: this.editorMenuOptions,
          defaultDirection: this.defaultDirection,
          autoDetectLanguage: this.autoDetectLanguage,
        }),
      });
    },
    createEditorView() {
      const nodeViews = {};
      if (this.useHtmlMode) {
        nodeViews.html_block = (node, view, getPos) => {
          const nv = new HtmlBlockView(node, view, getPos);
          this.htmlBlockView = nv;
          this.htmlBlockViews.push(nv);
          const destroy = nv.destroy.bind(nv);
          nv.destroy = () => {
            destroy();
            this.htmlBlockViews = this.htmlBlockViews.filter(v => v !== nv);
          };
          return nv;
        };
      }
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.state,
        nodeViews,
        dispatchTransaction: tx => {
          this.state = this.state.apply(tx);
          this.editorView.updateState(this.state);
          this.emitOnChange();
        },
        handleDOMEvents: {
          keyup: () => {
            this.onKeyup();
          },
          keydown: (view, event) => {
            this.onKeydown(event);
          },
          focus: () => {
            this.onFocus();
          },
          blur: () => {
            this.onBlur();
          },
          paste: (view, event) => {
            const data = event.clipboardData.files;
            if (data.length > 0) {
              event.preventDefault();
            }
          },
        },
      });

      if (this.useHtmlMode) {
        this.setupToolbarInterception();
      }
    },
    setupToolbarInterception() {
      const editorRoot = this.$refs.editor;
      if (!editorRoot) return;

      this.toolbarHandler = e => {
        if (!this.htmlBlockView) return;

        const menubar = editorRoot.querySelector('.ProseMirror-menubar');
        const inHtmlBlock = this.htmlBlockView.dom.contains(e.target);
        const inMenubar = menubar?.contains(e.target);

        if (!inHtmlBlock && !inMenubar) {
          this.htmlBlockView.savedRange = null;
          return;
        }

        if (!this.htmlBlockView.savedRange || !inMenubar) return;

        const result = resolveExecCommand(e.target, menubar);
        if (result) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.htmlBlockView.restoreAndExec(result, editorRoot);
        }
      };

      editorRoot.addEventListener('mousedown', this.toolbarHandler, true);
      document.addEventListener('mousedown', this.toolbarHandler, true);
    },
    teardownToolbarInterception() {
      if (!this.toolbarHandler) return;
      const editorRoot = this.$refs.editor;
      if (editorRoot) {
        editorRoot.removeEventListener('mousedown', this.toolbarHandler, true);
      }
      document.removeEventListener('mousedown', this.toolbarHandler, true);
      this.toolbarHandler = null;
    },
    reloadState(content = this.value) {
      if (this.useHtmlMode) {
        this.state = this.createHtmlState(content);
        this.editorView.updateState(this.state);
        this.focusEditor(content);
        return;
      }
      // eslint-disable-next-line prefer-rest-params
      this.$options.extends.methods.reloadState.apply(this, arguments);
    },
    appendHtmlBlock(html) {
      if (!html || !this.editorView) return;
      const node = messageSchema.nodes.html_block.create({ html });
      const { tr } = this.editorView.state;
      tr.insert(tr.doc.content.size, node);
      this.editorView.dispatch(tr);
    },
    serializeToHtml() {
      if (!this.editorView) return '';
      const serializer = DOMSerializer.fromSchema(messageSchema);
      const container = document.createElement('div');

      this.editorView.state.doc.forEach((node, offset) => {
        if (node.type.name === 'html_block') {
          const nv = this.htmlBlockViews.find(v => v.getPos() === offset);
          const html = nv ? nv.getHtml() : node.attrs.html;
          container.insertAdjacentHTML('beforeend', html);
        } else {
          container.appendChild(serializer.serializeNode(node));
        }
      });

      return container.innerHTML;
    },
    setAlignment(alignment) {
      if (!this.editorView) return;
      const { state, dispatch } = this.editorView;
      setBlockType(state.schema.nodes.paragraph, {
        align: alignment,
        autoDetected: false,
      })(state, dispatch);
      this.editorView.focus();
    },
    emojiOnClick(emoji) {
      // Insert emoji directly into the editor then close the picker
      this.insertContentIntoEditor(emoji);
      this.editorView?.focus();
      this.hideEmojiPicker();
    },
    toggleEditorToolbar() {
      this.showToolbarInternal = !this.showToolbarInternal;
    },
    onClickAwayEmojiPicker() {
      if (this.showEmojiPicker) this.hideEmojiPicker();
    },
    setDocumentDirection(direction) {
      if (!this.editorView) return;
      const { state, dispatch } = this.editorView;
      const { bullet_list, ordered_list, paragraph } = state.schema.nodes;
      const { $from } = state.selection;
      const align = direction === 'rtl' ? 'right' : 'left';
      const tr = state.tr;

      const paragraphAttrs = node => ({
        ...node.attrs,
        direction,
        align,
        autoDetected: false,
      });

      let enclosingList = null;
      for (let d = $from.depth; d >= 0; d -= 1) {
        const node = $from.node(d);
        if (node.type === bullet_list || node.type === ordered_list) {
          enclosingList = { node, pos: $from.before(d) };
          break;
        }
      }

      if (enclosingList) {
        const { node: listNode, pos: listPos } = enclosingList;
        tr.setNodeMarkup(listPos, null, { ...listNode.attrs, dir: direction });
        listNode.descendants((child, childPos) => {
          if (child.type === paragraph) {
            tr.setNodeMarkup(
              listPos + 1 + childPos,
              null,
              paragraphAttrs(child)
            );
          }
          return true;
        });
      } else if ($from.parent.type === paragraph) {
        tr.setNodeMarkup($from.before(), null, paragraphAttrs($from.parent));
      }

      if (tr.docChanged) dispatch(tr);
      this.editorView.focus();
    },
    switchTypingDirection(direction) {
      this.typingDirection = direction;
      this.setDocumentDirection(direction);
    },
  },
};
</script>
<style lang="scss" scoped>
.editor-root {
  min-height: unset;
  cursor: default;
}
.message-editor {
  @apply px-0 border-0;

  &.note-mode {
    ::v-deep {
      .ProseMirror {
        &.ProseMirror-woot-style {
          @apply bg-yellow-50;
        }
      }
    }

    .message-toolbar {
      @apply bg-yellow-50;
    }
  }
}

.editor-wrap {
  margin-bottom: 0 !important;
}

::v-deep {
  .html-block-content {
    padding: 12px 16px;
    outline: none;
    overflow: auto;

    :where(*) {
      all: revert;
    }
  }

  .ProseMirror {
    padding: 12px 16px;

    &.ProseMirror-woot-style {
      min-height: 100px;
      max-height: calc(100vh - 580px);
    }

    p {
      font-size: 16px;
      margin-top: 0px;
      margin-bottom: 0px;
    }

    .empty-node[style='text-align: right'] {
      &::before {
        right: 16px;
      }
    }
    .empty-node[style='text-align: left'] {
      &::before {
        left: 16px;
      }
    }
  }

  .hide-toolbar {
    .ProseMirror-menubar {
      display: none;
    }
  }

  .typing-direction-rtl {
    p {
      direction: rtl;
    }

    .empty-node[style*='text-align: left'] {
      &::before {
        left: 16px;
      }
    }
  }

  .typing-direction-ltr {
    p {
      direction: ltr;
    }
    .empty-node[style*='text-align: left'] {
      &::before {
        left: 16px;
      }
    }
  }

  .prosemirror-mention-node {
    unicode-bidi: isolate;
  }

  .ProseMirror-menubar {
    position: absolute;
    left: 70px;
    bottom: 5px;
    top: unset;
    width: fit-content;
    background: #F3F4F6;
    border-radius: 8px;
    padding: 7px 8px;
    display: flex;
    align-items: center;
    border-bottom: none;

    .ProseMirror-menuitem {
      min-width: 24px;
      height: 24px;
      align-items: center;
      display: flex;
      justify-content: center;
      border-radius: 4px;

      .ProseMirror-icon {
        padding: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;

        &.ProseMirror-menu-active {
          @apply bg-gray-200;
        }

        svg {
          width: unset !important;
          height: unset !important;
          use {
            scale: 150%;
          }
        }
      }
    }

    .ProseMirror-menu-dropdown-menu {
      top: unset;
      bottom: 100%;
      max-height: 200px;
      overflow: auto;
      border-radius: 8px;
      width: 140px;
      padding: 4px;
      background: #F3F4F6;
      box-shadow: 0 1px 3px 0 rgba(15, 24, 40, 0.10), 0 1px 2px 0 rgba(15, 24, 40, 0.06);
      border: 0;

      .ProseMirror-menu-dropdown-item {
        @apply text-sm text-gray-600 leading-5;

        &:hover {
          @apply bg-gray-200;
        }
      }

      &.alignment-menu.menu-item-alignment-option {
        gap: 8px;
        .ProseMirror-menu-dropdown-item {
          height: 24px;
        }
      }
    }
  }

  .template-parser-actions {
    @apply absolute bottom-0 left-0 w-full px-6 pt-4 pb-6 bg-white;
  }

  .search-contact-email {
    @apply rounded-[8px] border-gray-300 h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400 mb-0;
  }

  &[dir='rtl'] {
    .ProseMirror-menubar {
      position: absolute;
      right: 58px;
      left: 0;
    }
  }
}

.message-toolbar {
  @apply flex flex-col w-full;

  ::v-deep .message-toolbar-item {
    @apply text-[24px] text-[#6B7280] hover:bg-neutral-100 rounded-md cursor-pointer p-1 select-none;

    &.active {
      @apply bg-gray-100;
    }

    &.disabled {
      color: #dfdfdf;
      cursor: not-allowed;
      user-select: none;
    }
  }
}

.error {
  @apply text-[#B91C1C] border-[#B91C1C];

  &.message {
    @apply text-sm;
  }
}
</style>
